import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router"
import { Megaphone, Plus, Search, X } from "lucide-react"
import * as React from "react"
import { PageHeader } from "@/components/layout"
import { QuickFilter } from "@/components/promotions/grid-view-actions"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { CouponsAdminGrid } from "@/features/coupons-admin/CouponsAdminGrid"
import type { CouponsAdminInput } from "@/features/coupons-admin/coupons-admin-datasource"
import { useDebounce } from "@/hooks/use-debounce"

export const Route = createFileRoute("/coupons/")({
	component: CouponsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePage(search.page),
		search: search.search as string | undefined,
		// Column-managed filters (CSV-encoded values, owned by the grid).
		status: search.status as string | undefined,
		type: search.type as string | undefined,
		region: search.region as string | undefined,
		audience: search.audience as string | undefined,
		// Route-owned: which coupons to list. Defaults to manual so
		// program-issued coupons don't flood the grid.
		source: normalizeSource(search.source),
		programId: search.programId as string | undefined,
	}),
})

type CouponSourceParam = "manual" | "program" | "all"

function normalizeSource(raw: unknown): CouponSourceParam | undefined {
	return raw === "program" || raw === "all" || raw === "manual"
		? raw
		: undefined
}

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

function DebouncedSearchInput({
	defaultValue,
	onDebouncedChange,
	inputRef,
}: {
	defaultValue: string
	onDebouncedChange: (value: string) => void
	inputRef?: React.RefObject<{ clear: () => void } | null>
}) {
	const [value, setValue] = React.useState(defaultValue)
	const debouncedValue = useDebounce(value, 150)
	const isInitialMount = React.useRef(true)

	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
			return
		}
		onDebouncedChange(debouncedValue)
	}, [debouncedValue, onDebouncedChange])

	React.useImperativeHandle(inputRef, () => ({
		clear: () => setValue(""),
	}))

	return (
		<div className="relative w-full max-w-[280px] min-w-[200px]">
			<Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-meta" />
			<Input
				placeholder="Search code or description"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="pl-8"
			/>
		</div>
	)
}

function CouponsPage() {
	const searchParams = useSearch({ from: "/coupons/" })
	const navigate = useNavigate()

	const pageIndex = (searchParams.page ?? 1) - 1
	const statusFilter = searchParams.status ?? ""
	const typeFilter = searchParams.type ?? ""

	const searchRef = React.useRef<{ clear: () => void }>(null)

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/coupons",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) => {
			updateFilters({ search: value || undefined, page: undefined })
		},
		[updateFilters],
	)

	// Only non-column filters reach the grid via `input`. Column filters
	// (status / type / region) come from the URL and are applied via
	// initialState + the grid's URL-sync effect.
	const gridInput = React.useMemo<CouponsAdminInput>(() => {
		const input: CouponsAdminInput = {}
		if (searchParams.search) input.search = searchParams.search
		if (searchParams.programId) {
			input.programId = searchParams.programId
		} else {
			const source = searchParams.source ?? "manual"
			if (source !== "all") input.source = source
		}
		return input
	}, [searchParams.search, searchParams.source, searchParams.programId])

	const handlePageChange = React.useCallback(
		(nextPageIndex: number) => {
			updateFilters({
				page: nextPageIndex > 0 ? nextPageIndex + 1 : undefined,
			})
		},
		[updateFilters],
	)

	const clearFilters = React.useCallback(() => {
		searchRef.current?.clear()
		navigate({ to: "/coupons", search: {} as never, replace: true })
	}, [navigate])

	const hasActiveFilters =
		!!searchParams.status ||
		!!searchParams.type ||
		!!searchParams.region ||
		!!searchParams.audience ||
		!!searchParams.source ||
		!!searchParams.programId ||
		!!searchParams.search

	const programIssued = searchParams.source === "program"
	const toggleProgramIssued = React.useCallback(() => {
		updateFilters({
			source: programIssued ? undefined : "program",
			programId: undefined,
			page: undefined,
		})
	}, [programIssued, updateFilters])

	const toggleChip = React.useCallback(
		(key: "status" | "type", value: string) => {
			const current = searchParams[key]
			updateFilters({
				[key]: current === value ? undefined : value,
				page: undefined,
			})
		},
		[searchParams, updateFilters],
	)

	const toolbar = (
		<>
			<DebouncedSearchInput
				defaultValue={searchParams.search ?? ""}
				onDebouncedChange={handleDebouncedSearch}
				inputRef={searchRef}
			/>
			<div className="flex items-center gap-1">
				<QuickFilter
					active={statusFilter === "active"}
					onToggle={() => toggleChip("status", "active")}
				>
					Active
				</QuickFilter>
				<QuickFilter
					active={statusFilter === "inactive"}
					onToggle={() => toggleChip("status", "inactive")}
				>
					Inactive
				</QuickFilter>
				<QuickFilter
					active={typeFilter === "percentage"}
					onToggle={() => toggleChip("type", "percentage")}
				>
					Percentage
				</QuickFilter>
				<QuickFilter
					active={typeFilter === "fixed_amount"}
					onToggle={() => toggleChip("type", "fixed_amount")}
				>
					Fixed amount
				</QuickFilter>
				<QuickFilter active={programIssued} onToggle={toggleProgramIssued}>
					Program-issued
				</QuickFilter>
			</div>
			{hasActiveFilters && (
				<Button variant="ghost" onClick={clearFilters}>
					<X />
					Clear
				</Button>
			)}
		</>
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Coupons"
				actions={
					<>
						<Button variant="outline" asChild>
							<Link to="/coupons/programs">
								<Megaphone />
								Programs
							</Link>
						</Button>
						<Button asChild>
							<Link to="/coupons/new" search={{ from: undefined }}>
								<Plus />
								Create coupon
							</Link>
						</Button>
					</>
				}
			/>

			<div className="min-h-0 flex-1">
				<CouponsAdminGrid
					input={gridInput}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					toolbar={toolbar}
				/>
			</div>
		</div>
	)
}
